import {
  homepageSectionTemplateKinds,
  homepageSchemaVersion,
  type HomepageAddedSection,
  type HomepageContent,
  type HomepageSectionHeading,
  type HomepageTitleBodyItem,
} from "@whitehaven/contracts/homepage";

const MAX_HOMEPAGE_JSON_BYTES = 128 * 1024;
const forbiddenControlCharacters =
  /[\u0000-\u001f\u007f-\u009f\u00ad\u034f\u061c\u115f\u1160\u17b4\u17b5\u180e\u200b-\u200f\u2028-\u202e\u2060-\u206f\ufeff\ufff9-\ufffb]/u;
const htmlLikeMarkup = /<\/?[a-z][^>]*>/iu;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
const safeSectionIdPattern = /^[a-z0-9][a-z0-9-]{7,63}$/u;
const safeInternalHrefPattern =
  /^(?:\/[A-Za-z0-9/_-]*|#[A-Za-z][A-Za-z0-9_-]*)$/u;

type JsonObject = Readonly<Record<string, unknown>>;

export interface HomepageValidationIssue {
  readonly path: string;
  readonly code:
    | "INVALID_TYPE"
    | "MISSING_FIELD"
    | "UNKNOWN_FIELD"
    | "INVALID_VALUE"
    | "INVALID_LENGTH"
    | "NOT_CANONICAL"
    | "DUPLICATE_VALUE";
  readonly message: string;
}

export interface PreparedHomepageContent {
  readonly content: HomepageContent;
  readonly canonicalJson: string;
  readonly contentSha256: string;
}

export class HomepageContentValidationError extends Error {
  readonly issues: readonly HomepageValidationIssue[];

  constructor(issues: readonly HomepageValidationIssue[]) {
    super("Homepage content did not pass schema validation.");
    this.name = "HomepageContentValidationError";
    this.issues = issues;
  }
}

function addIssue(
  issues: HomepageValidationIssue[],
  path: string,
  code: HomepageValidationIssue["code"],
  message: string,
): void {
  issues.push({ path, code, message });
}

function objectValue(
  value: unknown,
  path: string,
  allowedKeys: readonly string[],
  issues: HomepageValidationIssue[],
  requiredKeys: readonly string[] = allowedKeys,
): JsonObject {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    addIssue(issues, path, "INVALID_TYPE", "Expected an object.");
    return {};
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    addIssue(issues, path, "INVALID_TYPE", "Expected a plain object.");
    return {};
  }
  const record = value as JsonObject;
  const allowed = new Set(allowedKeys);
  for (const key of Object.keys(record)) {
    if (!allowed.has(key)) {
      addIssue(
        issues,
        `${path}.${key}`,
        "UNKNOWN_FIELD",
        "This field is not part of the V2.1 homepage.",
      );
    }
  }
  for (const key of requiredKeys) {
    if (!Object.hasOwn(record, key)) {
      addIssue(
        issues,
        `${path}.${key}`,
        "MISSING_FIELD",
        "This field is required.",
      );
    }
  }
  return record;
}

function stringValue(
  record: JsonObject,
  key: string,
  path: string,
  minimumLength: number,
  maximumLength: number,
  issues: HomepageValidationIssue[],
): string {
  const value = record[key];
  const fieldPath = `${path}.${key}`;
  if (typeof value !== "string") {
    if (Object.hasOwn(record, key)) {
      addIssue(issues, fieldPath, "INVALID_TYPE", "Expected plain text.");
    }
    return "";
  }
  if (value !== value.trim()) {
    addIssue(
      issues,
      fieldPath,
      "NOT_CANONICAL",
      "Remove leading or trailing whitespace.",
    );
  }
  if (value !== value.normalize("NFC")) {
    addIssue(issues, fieldPath, "NOT_CANONICAL", "Use canonical Unicode text.");
  }
  if (forbiddenControlCharacters.test(value)) {
    addIssue(
      issues,
      fieldPath,
      "INVALID_VALUE",
      "Control and bidirectional override characters are not allowed.",
    );
  }
  if (htmlLikeMarkup.test(value)) {
    addIssue(
      issues,
      fieldPath,
      "INVALID_VALUE",
      "HTML and script-like markup are not allowed.",
    );
  }
  const length = countHomepageTextCharacters(value);
  if (length < minimumLength || length > maximumLength) {
    addIssue(
      issues,
      fieldPath,
      "INVALID_LENGTH",
      `Use ${minimumLength}–${maximumLength} characters.`,
    );
  }
  return value;
}

function enumValue<T extends string>(
  record: JsonObject,
  key: string,
  path: string,
  allowedValues: readonly T[],
  fallback: T,
  issues: HomepageValidationIssue[],
): T {
  const value = record[key];
  if (typeof value !== "string" || !allowedValues.includes(value as T)) {
    addIssue(
      issues,
      `${path}.${key}`,
      "INVALID_VALUE",
      `Choose one of: ${allowedValues.join(", ")}.`,
    );
    return fallback;
  }
  return value as T;
}

function boundedArray<T>(
  value: unknown,
  path: string,
  minimumLength: number,
  maximumLength: number,
  parser: (item: unknown, itemPath: string) => T,
  issues: HomepageValidationIssue[],
): readonly T[] {
  if (!Array.isArray(value)) {
    addIssue(issues, path, "INVALID_TYPE", "Expected a list.");
    return [];
  }
  if (value.length < minimumLength || value.length > maximumLength) {
    addIssue(
      issues,
      path,
      "INVALID_LENGTH",
      `Use ${minimumLength}–${maximumLength} items.`,
    );
  }
  return value
    .slice(0, maximumLength)
    .map((item, index) => parser(item, `${path}[${index}]`));
}

export function countHomepageTextCharacters(value: string): number {
  return Array.from(value).length;
}

function tuple<T, N extends number>(
  value: unknown,
  path: string,
  length: N,
  parser: (item: unknown, itemPath: string) => T,
  issues: HomepageValidationIssue[],
): readonly T[] {
  const array = Array.isArray(value) ? value : [];
  if (!Array.isArray(value)) {
    addIssue(
      issues,
      path,
      "INVALID_TYPE",
      `Expected a list of ${length} items.`,
    );
  } else if (array.length !== length) {
    addIssue(
      issues,
      path,
      "INVALID_LENGTH",
      `The V2.1 design requires exactly ${length} items.`,
    );
  }
  return Array.from({ length }, (_unused, index) =>
    parser(array[index], `${path}[${index}]`),
  );
}

function titleBodyValue(
  value: unknown,
  path: string,
  issues: HomepageValidationIssue[],
): HomepageTitleBodyItem {
  const record = objectValue(value, path, ["title", "body"], issues);
  return {
    title: stringValue(record, "title", path, 2, 120, issues),
    body: stringValue(record, "body", path, 2, 700, issues),
  };
}

function sectionHeadingValue(
  value: unknown,
  path: string,
  issues: HomepageValidationIssue[],
): HomepageSectionHeading {
  const record = objectValue(
    value,
    path,
    ["eyebrow", "heading", "body"],
    issues,
  );
  return {
    eyebrow: stringValue(record, "eyebrow", path, 2, 100, issues),
    heading: stringValue(record, "heading", path, 5, 160, issues),
    body: stringValue(record, "body", path, 10, 800, issues),
  };
}

function stringTuple(
  value: unknown,
  path: string,
  length: 3 | 4,
  issues: HomepageValidationIssue[],
  maximumLength = 180,
): readonly string[] {
  return tuple(
    value,
    path,
    length,
    (item, itemPath) => {
      const holder = { value: item };
      return stringValue(holder, "value", itemPath, 2, maximumLength, issues);
    },
    issues,
  );
}

function reportDuplicateValues(
  values: readonly string[],
  path: string,
  issues: HomepageValidationIssue[],
): void {
  const seen = new Set<string>();
  for (const [index, value] of values.entries()) {
    const normalized = value.toLocaleLowerCase("en-US");
    if (normalized && seen.has(normalized)) {
      addIssue(
        issues,
        `${path}[${index}]`,
        "DUPLICATE_VALUE",
        "Each item must have distinct visitor-facing text.",
      );
    }
    seen.add(normalized);
  }
}

export function parseHomepageContent(value: unknown): HomepageContent {
  const issues: HomepageValidationIssue[] = [];
  const root = objectValue(
    value,
    "$",
    [
      "schemaVersion",
      "theme",
      "seo",
      "hero",
      "services",
      "bookingSteps",
      "packages",
      "careBoundary",
      "guides",
      "faq",
      "contact",
      "footer",
      "addedSections",
    ],
    issues,
  );

  if (root.schemaVersion !== homepageSchemaVersion) {
    addIssue(
      issues,
      "$.schemaVersion",
      "INVALID_VALUE",
      "Only the live V2.1 homepage schema is supported.",
    );
  }

  const themeRecord = objectValue(
    root.theme,
    "$.theme",
    ["palette", "typography", "spacing", "buttons", "cards"],
    issues,
  );
  const theme: HomepageContent["theme"] = {
    palette: enumValue(
      themeRecord,
      "palette",
      "$.theme",
      ["whitehaven", "ocean", "island"],
      "whitehaven",
      issues,
    ),
    typography: enumValue(
      themeRecord,
      "typography",
      "$.theme",
      ["serif-accent", "modern-sans"],
      "serif-accent",
      issues,
    ),
    spacing: enumValue(
      themeRecord,
      "spacing",
      "$.theme",
      ["balanced", "compact", "airy"],
      "balanced",
      issues,
    ),
    buttons: enumValue(
      themeRecord,
      "buttons",
      "$.theme",
      ["rounded", "pill"],
      "rounded",
      issues,
    ),
    cards: enumValue(
      themeRecord,
      "cards",
      "$.theme",
      ["soft", "outlined"],
      "soft",
      issues,
    ),
  };

  const seoRecord = objectValue(
    root.seo,
    "$.seo",
    ["title", "description"],
    issues,
  );
  const seo = {
    title: stringValue(seoRecord, "title", "$.seo", 5, 70, issues),
    description: stringValue(
      seoRecord,
      "description",
      "$.seo",
      20,
      180,
      issues,
    ),
  };

  const heroRecord = objectValue(
    root.hero,
    "$.hero",
    [
      "eyebrow",
      "headingLead",
      "headingAccent",
      "lead",
      "primaryActionLabel",
      "secondaryActionLabel",
      "trustItems",
      "visitCard",
      "trustStrip",
    ],
    issues,
  );
  const visitCardRecord = objectValue(
    heroRecord.visitCard,
    "$.hero.visitCard",
    ["title", "status", "steps"],
    issues,
  );
  const heroSteps = tuple(
    visitCardRecord.steps,
    "$.hero.visitCard.steps",
    3,
    (item, path) => titleBodyValue(item, path, issues),
    issues,
  ) as HomepageContent["hero"]["visitCard"]["steps"];
  const heroTrustItems = stringTuple(
    heroRecord.trustItems,
    "$.hero.trustItems",
    3,
    issues,
  ) as HomepageContent["hero"]["trustItems"];
  const trustStrip = stringTuple(
    heroRecord.trustStrip,
    "$.hero.trustStrip",
    4,
    issues,
  ) as HomepageContent["hero"]["trustStrip"];
  reportDuplicateValues(heroTrustItems, "$.hero.trustItems", issues);
  reportDuplicateValues(trustStrip, "$.hero.trustStrip", issues);
  const hero = {
    eyebrow: stringValue(heroRecord, "eyebrow", "$.hero", 2, 100, issues),
    headingLead: stringValue(
      heroRecord,
      "headingLead",
      "$.hero",
      3,
      100,
      issues,
    ),
    headingAccent: stringValue(
      heroRecord,
      "headingAccent",
      "$.hero",
      3,
      100,
      issues,
    ),
    lead: stringValue(heroRecord, "lead", "$.hero", 10, 400, issues),
    primaryActionLabel: stringValue(
      heroRecord,
      "primaryActionLabel",
      "$.hero",
      2,
      60,
      issues,
    ),
    secondaryActionLabel: stringValue(
      heroRecord,
      "secondaryActionLabel",
      "$.hero",
      2,
      60,
      issues,
    ),
    trustItems: heroTrustItems,
    visitCard: {
      title: stringValue(
        visitCardRecord,
        "title",
        "$.hero.visitCard",
        3,
        100,
        issues,
      ),
      status: stringValue(
        visitCardRecord,
        "status",
        "$.hero.visitCard",
        2,
        40,
        issues,
      ),
      steps: heroSteps,
    },
    trustStrip,
  };

  const services = sectionHeadingValue(root.services, "$.services", issues);

  const bookingRecord = objectValue(
    root.bookingSteps,
    "$.bookingSteps",
    ["eyebrow", "heading", "body", "steps"],
    issues,
  );
  const bookingSteps = {
    eyebrow: stringValue(
      bookingRecord,
      "eyebrow",
      "$.bookingSteps",
      2,
      100,
      issues,
    ),
    heading: stringValue(
      bookingRecord,
      "heading",
      "$.bookingSteps",
      5,
      160,
      issues,
    ),
    body: stringValue(bookingRecord, "body", "$.bookingSteps", 10, 800, issues),
    steps: tuple(
      bookingRecord.steps,
      "$.bookingSteps.steps",
      3,
      (item, path) => titleBodyValue(item, path, issues),
      issues,
    ) as HomepageContent["bookingSteps"]["steps"],
  };

  const packages = sectionHeadingValue(root.packages, "$.packages", issues);

  const careRecord = objectValue(
    root.careBoundary,
    "$.careBoundary",
    ["eyebrow", "heading", "body", "cards"],
    issues,
  );
  const careCards = tuple(
    careRecord.cards,
    "$.careBoundary.cards",
    2,
    (item, path) => {
      const record = objectValue(
        item,
        path,
        ["label", "title", "body", "bullets"],
        issues,
      );
      return {
        label: stringValue(record, "label", path, 2, 100, issues),
        title: stringValue(record, "title", path, 2, 160, issues),
        body: stringValue(record, "body", path, 10, 700, issues),
        bullets: stringTuple(
          record.bullets,
          `${path}.bullets`,
          3,
          issues,
          220,
        ) as readonly [string, string, string],
      };
    },
    issues,
  ) as HomepageContent["careBoundary"]["cards"];
  const careBoundary = {
    eyebrow: stringValue(
      careRecord,
      "eyebrow",
      "$.careBoundary",
      2,
      100,
      issues,
    ),
    heading: stringValue(
      careRecord,
      "heading",
      "$.careBoundary",
      5,
      160,
      issues,
    ),
    body: stringValue(careRecord, "body", "$.careBoundary", 10, 800, issues),
    cards: careCards,
  };

  const guideRecord = objectValue(
    root.guides,
    "$.guides",
    ["eyebrow", "heading", "body", "cards"],
    issues,
  );
  const guideCards = tuple(
    guideRecord.cards,
    "$.guides.cards",
    3,
    (item, path) => {
      const record = objectValue(
        item,
        path,
        ["eyebrow", "title", "body", "meta", "actionLabel"],
        issues,
      );
      return {
        eyebrow: stringValue(record, "eyebrow", path, 2, 100, issues),
        title: stringValue(record, "title", path, 2, 160, issues),
        body: stringValue(record, "body", path, 10, 700, issues),
        meta: stringValue(record, "meta", path, 2, 100, issues),
        actionLabel: stringValue(record, "actionLabel", path, 2, 60, issues),
      };
    },
    issues,
  ) as HomepageContent["guides"]["cards"];
  const guides = {
    eyebrow: stringValue(guideRecord, "eyebrow", "$.guides", 2, 100, issues),
    heading: stringValue(guideRecord, "heading", "$.guides", 5, 160, issues),
    body: stringValue(guideRecord, "body", "$.guides", 10, 800, issues),
    cards: guideCards,
  };

  const faqRecord = objectValue(
    root.faq,
    "$.faq",
    ["eyebrow", "heading", "body", "actionLabel", "items"],
    issues,
  );
  const faqItems = tuple(
    faqRecord.items,
    "$.faq.items",
    5,
    (item, path) => {
      const record = objectValue(item, path, ["question", "answer"], issues);
      return {
        question: stringValue(record, "question", path, 5, 180, issues),
        answer: stringValue(record, "answer", path, 10, 900, issues),
      };
    },
    issues,
  ) as HomepageContent["faq"]["items"];
  reportDuplicateValues(
    faqItems.map((item) => item.question),
    "$.faq.items",
    issues,
  );
  const faq = {
    eyebrow: stringValue(faqRecord, "eyebrow", "$.faq", 2, 100, issues),
    heading: stringValue(faqRecord, "heading", "$.faq", 5, 160, issues),
    body: stringValue(faqRecord, "body", "$.faq", 10, 800, issues),
    actionLabel: stringValue(faqRecord, "actionLabel", "$.faq", 2, 60, issues),
    items: faqItems,
  };

  const contactRecord = objectValue(
    root.contact,
    "$.contact",
    [
      "eyebrow",
      "heading",
      "body",
      "email",
      "privacyNotice",
      "availabilityNotice",
      "formHelp",
      "consentLabel",
      "actionLabel",
    ],
    issues,
  );
  const contactEmail = stringValue(
    contactRecord,
    "email",
    "$.contact",
    5,
    254,
    issues,
  );
  if (contactEmail && !emailPattern.test(contactEmail)) {
    addIssue(
      issues,
      "$.contact.email",
      "INVALID_VALUE",
      "Use a valid public email address.",
    );
  }
  const contact = {
    eyebrow: stringValue(contactRecord, "eyebrow", "$.contact", 2, 100, issues),
    heading: stringValue(contactRecord, "heading", "$.contact", 5, 160, issues),
    body: stringValue(contactRecord, "body", "$.contact", 10, 800, issues),
    email: contactEmail,
    privacyNotice: stringValue(
      contactRecord,
      "privacyNotice",
      "$.contact",
      10,
      300,
      issues,
    ),
    availabilityNotice: stringValue(
      contactRecord,
      "availabilityNotice",
      "$.contact",
      10,
      400,
      issues,
    ),
    formHelp: stringValue(
      contactRecord,
      "formHelp",
      "$.contact",
      5,
      180,
      issues,
    ),
    consentLabel: stringValue(
      contactRecord,
      "consentLabel",
      "$.contact",
      10,
      220,
      issues,
    ),
    actionLabel: stringValue(
      contactRecord,
      "actionLabel",
      "$.contact",
      2,
      60,
      issues,
    ),
  };

  const footerRecord = objectValue(
    root.footer,
    "$.footer",
    ["description", "legalLine"],
    issues,
  );
  const footer = {
    description: stringValue(
      footerRecord,
      "description",
      "$.footer",
      10,
      400,
      issues,
    ),
    legalLine: stringValue(
      footerRecord,
      "legalLine",
      "$.footer",
      10,
      200,
      issues,
    ),
  };

  const addedSections = boundedArray(
    root.addedSections,
    "$.addedSections",
    0,
    12,
    (value, path): HomepageAddedSection => {
      const baseRecord = objectValue(
        value,
        path,
        [
          "id",
          "kind",
          "eyebrow",
          "heading",
          "body",
          "presentation",
          "actionLabel",
          "actionHref",
          "cards",
          "paragraphs",
          "items",
        ],
        issues,
        ["id", "kind", "eyebrow", "heading", "body", "presentation"],
      );
      const id = stringValue(baseRecord, "id", path, 8, 64, issues);
      if (id && !safeSectionIdPattern.test(id)) {
        addIssue(
          issues,
          `${path}.id`,
          "INVALID_VALUE",
          "Use a lowercase letters, numbers, and hyphens section identifier.",
        );
      }
      const kind = enumValue(
        baseRecord,
        "kind",
        path,
        homepageSectionTemplateKinds,
        "richText",
        issues,
      );
      const base = {
        id,
        kind,
        eyebrow: stringValue(baseRecord, "eyebrow", path, 2, 100, issues),
        heading: stringValue(baseRecord, "heading", path, 5, 160, issues),
        body: stringValue(baseRecord, "body", path, 10, 800, issues),
        presentation: (() => {
          const record = objectValue(
            baseRecord.presentation,
            `${path}.presentation`,
            ["layout", "alignment", "spacing", "accent"],
            issues,
          );
          return {
            layout: enumValue(
              record,
              "layout",
              `${path}.presentation`,
              ["auto", "two-column", "three-column"],
              "auto",
              issues,
            ),
            alignment: enumValue(
              record,
              "alignment",
              `${path}.presentation`,
              ["left", "center"],
              "left",
              issues,
            ),
            spacing: enumValue(
              record,
              "spacing",
              `${path}.presentation`,
              ["standard", "compact", "airy"],
              "standard",
              issues,
            ),
            accent: enumValue(
              record,
              "accent",
              `${path}.presentation`,
              ["none", "leaf", "sun", "wave"],
              "none",
              issues,
            ),
          } as const;
        })(),
      };
      if (kind === "hero" || kind === "callToAction") {
        const actionRecord = objectValue(
          value,
          path,
          [
            "id",
            "kind",
            "eyebrow",
            "heading",
            "body",
            "presentation",
            "actionLabel",
            "actionHref",
          ],
          issues,
        );
        const actionHref = stringValue(
          actionRecord,
          "actionHref",
          path,
          1,
          180,
          issues,
        );
        if (actionHref && !safeInternalHrefPattern.test(actionHref)) {
          addIssue(
            issues,
            `${path}.actionHref`,
            "INVALID_VALUE",
            "Only an internal path or in-page anchor is allowed.",
          );
        }
        return {
          ...base,
          kind,
          actionLabel: stringValue(
            actionRecord,
            "actionLabel",
            path,
            2,
            60,
            issues,
          ),
          actionHref,
        };
      }
      if (kind === "cards") {
        const cardsRecord = objectValue(
          value,
          path,
          ["id", "kind", "eyebrow", "heading", "body", "presentation", "cards"],
          issues,
        );
        const cards = boundedArray(
          cardsRecord.cards,
          `${path}.cards`,
          2,
          6,
          (item, itemPath) => {
            const record = objectValue(
              item,
              itemPath,
              ["id", "title", "body"],
              issues,
            );
            const cardId = stringValue(record, "id", itemPath, 8, 64, issues);
            if (cardId && !safeSectionIdPattern.test(cardId)) {
              addIssue(
                issues,
                `${itemPath}.id`,
                "INVALID_VALUE",
                "Use a lowercase letters, numbers, and hyphens card identifier.",
              );
            }
            return {
              id: cardId,
              title: stringValue(record, "title", itemPath, 2, 120, issues),
              body: stringValue(record, "body", itemPath, 2, 700, issues),
            };
          },
          issues,
        );
        reportDuplicateValues(
          cards.map((card) => card.id),
          `${path}.cards`,
          issues,
        );
        return {
          ...base,
          kind,
          cards,
        };
      }
      if (kind === "faq") {
        const faqSectionRecord = objectValue(
          value,
          path,
          ["id", "kind", "eyebrow", "heading", "body", "presentation", "items"],
          issues,
        );
        const items = boundedArray(
          faqSectionRecord.items,
          `${path}.items`,
          1,
          8,
          (item, itemPath) => {
            const record = objectValue(
              item,
              itemPath,
              ["question", "answer"],
              issues,
            );
            return {
              question: stringValue(
                record,
                "question",
                itemPath,
                5,
                180,
                issues,
              ),
              answer: stringValue(record, "answer", itemPath, 10, 900, issues),
            };
          },
          issues,
        );
        reportDuplicateValues(
          items.map((item) => item.question),
          `${path}.items`,
          issues,
        );
        return { ...base, kind, items };
      }
      const richTextRecord = objectValue(
        value,
        path,
        [
          "id",
          "kind",
          "eyebrow",
          "heading",
          "body",
          "presentation",
          "paragraphs",
        ],
        issues,
      );
      return {
        ...base,
        kind: "richText",
        paragraphs: boundedArray(
          richTextRecord.paragraphs,
          `${path}.paragraphs`,
          1,
          6,
          (item, itemPath) =>
            stringValue({ value: item }, "value", itemPath, 10, 1_200, issues),
          issues,
        ),
      };
    },
    issues,
  );
  reportDuplicateValues(
    addedSections.map((section) => section.id),
    "$.addedSections",
    issues,
  );

  const content: HomepageContent = {
    schemaVersion: homepageSchemaVersion,
    theme,
    seo,
    hero,
    services,
    bookingSteps,
    packages,
    careBoundary,
    guides,
    faq,
    contact,
    footer,
    addedSections,
  };
  if (issues.length > 0) throw new HomepageContentValidationError(issues);
  return content;
}

export function parseHomepageContentJson(json: string): HomepageContent {
  if (new TextEncoder().encode(json).byteLength > MAX_HOMEPAGE_JSON_BYTES) {
    throw new HomepageContentValidationError([
      {
        path: "$",
        code: "INVALID_LENGTH",
        message: "Homepage content exceeds the 128 KiB limit.",
      },
    ]);
  }
  let value: unknown;
  try {
    value = JSON.parse(json);
  } catch {
    throw new HomepageContentValidationError([
      {
        path: "$",
        code: "INVALID_VALUE",
        message: "Homepage content must be valid JSON.",
      },
    ]);
  }
  return parseHomepageContent(value);
}

export interface HomepageChangeCapabilityRequirements {
  readonly create: boolean;
  readonly manage: boolean;
}

function sameJson(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function relativeOrderChanged(
  currentIds: readonly string[],
  nextIds: readonly string[],
): boolean {
  const currentSet = new Set(currentIds);
  const nextSet = new Set(nextIds);
  const currentShared = currentIds.filter((id) => nextSet.has(id));
  const nextShared = nextIds.filter((id) => currentSet.has(id));
  return !sameJson(currentShared, nextShared);
}

/**
 * Separates normal copy editing from structural CMS changes. Stable section
 * and card IDs make add/remove/reorder decisions enforceable at the API
 * boundary rather than relying on disabled controls in the browser.
 */
export function homepageChangeCapabilityRequirements(
  current: HomepageContent,
  next: HomepageContent,
): HomepageChangeCapabilityRequirements {
  let create = false;
  let manage = !sameJson(current.theme, next.theme);

  const currentIds = current.addedSections.map((section) => section.id);
  const nextIds = next.addedSections.map((section) => section.id);
  const currentById = new Map(
    current.addedSections.map((section) => [section.id, section] as const),
  );
  const nextById = new Map(
    next.addedSections.map((section) => [section.id, section] as const),
  );

  if (nextIds.some((id) => !currentById.has(id))) create = true;
  if (currentIds.some((id) => !nextById.has(id))) manage = true;
  if (relativeOrderChanged(currentIds, nextIds)) manage = true;

  for (const [sectionId, currentSection] of currentById) {
    const nextSection = nextById.get(sectionId);
    if (!nextSection) continue;
    if (currentSection.kind !== nextSection.kind) {
      manage = true;
      continue;
    }
    if (!sameJson(currentSection.presentation, nextSection.presentation)) {
      manage = true;
    }

    if (currentSection.kind === "cards" && nextSection.kind === "cards") {
      const currentCardIds = currentSection.cards.map((card) => card.id);
      const nextCardIds = nextSection.cards.map((card) => card.id);
      const currentCardSet = new Set(currentCardIds);
      const nextCardSet = new Set(nextCardIds);
      if (nextCardIds.some((id) => !currentCardSet.has(id))) create = true;
      if (currentCardIds.some((id) => !nextCardSet.has(id))) manage = true;
      if (relativeOrderChanged(currentCardIds, nextCardIds)) manage = true;
      continue;
    }

    if (currentSection.kind === "faq" && nextSection.kind === "faq") {
      if (nextSection.items.length > currentSection.items.length) create = true;
      if (nextSection.items.length < currentSection.items.length) manage = true;
      continue;
    }

    if (
      currentSection.kind === "richText" &&
      nextSection.kind === "richText"
    ) {
      if (nextSection.paragraphs.length > currentSection.paragraphs.length) {
        create = true;
      }
      if (nextSection.paragraphs.length < currentSection.paragraphs.length) {
        manage = true;
      }
    }
  }

  return { create, manage };
}

async function sha256Hex(json: string): Promise<string> {
  const encoded = new TextEncoder().encode(json);
  const owned = new Uint8Array(encoded.byteLength);
  owned.set(encoded);
  const digest = await crypto.subtle.digest("SHA-256", owned.buffer);
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

export function canonicalHomepageContentJson(value: unknown): string {
  return JSON.stringify(parseHomepageContent(value));
}

export async function prepareHomepageContent(
  value: unknown,
): Promise<PreparedHomepageContent> {
  const content = parseHomepageContent(value);
  const canonicalJson = JSON.stringify(content);
  return {
    content,
    canonicalJson,
    contentSha256: await sha256Hex(canonicalJson),
  };
}

export async function prepareHomepageContentJson(
  json: string,
): Promise<PreparedHomepageContent> {
  const content = parseHomepageContentJson(json);
  const canonicalJson = JSON.stringify(content);
  return {
    content,
    canonicalJson,
    contentSha256: await sha256Hex(canonicalJson),
  };
}
